import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getStatusBadgeClass, formatApiError } from '../../lib/utils';

const FOLLOW_UP_TYPE_OPTIONS = ['Call', 'Meeting', 'WhatsApp', 'Email'].map((v) => ({ value: v, label: v }));

const fmt2 = (n) => String(n).padStart(2, '0');
const isoDate = (d) => `${d.getFullYear()}-${fmt2(d.getMonth() + 1)}-${fmt2(d.getDate())}`;
const isoTime = (d) => `${fmt2(d.getHours())}:${fmt2(d.getMinutes())}`;

/**
 * Returns `{ type, date, time, description }` defaults for a status, or null if no
 * follow-up should be suggested for that status (Meeting Scheduled, Converted, Closed, New).
 */
export function defaultFollowUpForStatus(status, leadName = '') {
  const now = new Date();
  const inDays = (n, hh = 11, mm = 0) => {
    const d = new Date(now);
    d.setDate(now.getDate() + n);
    d.setHours(hh, mm, 0, 0);
    return d;
  };
  const tag = leadName ? ` with ${leadName}` : '';
  switch (status) {
    case "Didn't Pick Up":
      return { type: 'Call', date: isoDate(inDays(1)), time: '11:00', description: `Try the call${tag} again — earlier attempt went unanswered.` };
    case 'Busy / Call After Sometime': {
      const target = new Date(now);
      target.setHours(now.getHours() + 3, 0, 0, 0);
      const next = target.getHours() > 19 || target.getHours() < 8 ? inDays(1) : target;
      return { type: 'Call', date: isoDate(next), time: isoTime(next), description: `Call back${tag} — was busy earlier.` };
    }
    case 'Follow Up - Brochure Shared':
      return { type: 'Call', date: isoDate(inDays(2)), time: '11:00', description: `Discuss the brochure shared earlier${tag}.` };
    case 'Interested for Meeting':
      return { type: 'Meeting', date: isoDate(inDays(3)), time: '11:00', description: `Schedule the first meeting${tag}.` };
    case 'Meeting Done - Follow Up':
      return { type: 'Call', date: isoDate(inDays(3)), time: '11:00', description: `Post-meeting follow-up${tag}.` };
    default:
      return null;
  }
}

/**
 * Unified dialog for "change lead status" + (optional) "schedule follow-up".
 *
 * Props:
 *  - open, onOpenChange (controlled)
 *  - lead          { id, full_name, status }
 *  - newStatus     string e.g. "Didn't Pick Up"
 *  - onUpdated(updatedFields) — called after a successful save
 *  - statusAlreadyPersisted  boolean — when true the dialog only creates a follow-up
 *                            (caller has already saved the status). Defaults false.
 */
export default function LeadStatusFollowUpDialog({ open, onOpenChange, lead, newStatus, onUpdated, statusAlreadyPersisted = false }) {
  const defaults = useMemo(
    () => (newStatus ? defaultFollowUpForStatus(newStatus, lead?.full_name || lead?.company_name || '') : null),
    [newStatus, lead]
  );

  const [type, setType] = useState('Call');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('11:00');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset fields whenever the suggested defaults change (i.e. dialog opens for a new status)
  useEffect(() => {
    if (defaults) {
      setType(defaults.type);
      setDate(defaults.date);
      setTime(defaults.time);
      setDescription(defaults.description);
    }
  }, [defaults]);

  const close = () => {
    onOpenChange(false);
  };

  const persistStatusOnly = async () => {
    if (statusAlreadyPersisted) {
      // Nothing to do; just close.
      close();
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/leads/${lead.id}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      onUpdated && onUpdated({ status: newStatus });
      close();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const persistStatusAndFollowUp = async () => {
    if (!date || !time || !type) {
      toast.error('Date, time and type are required for the follow-up');
      return;
    }
    setSaving(true);
    try {
      // Skip status patch if caller already persisted it.
      if (!statusAlreadyPersisted) {
        await api.patch(`/leads/${lead.id}/status`, { status: newStatus });
      }
      await api.post('/lead-follow-ups', {
        lead_id: lead.id,
        follow_up_type: type,
        due_date: date,
        due_time: time,
        description,
        priority: 'Medium',
      });
      toast.success(
        statusAlreadyPersisted
          ? `${type} scheduled for ${date} ${time}`
          : `Status updated to ${newStatus} · ${type} scheduled for ${date} ${time}`
      );
      onUpdated && onUpdated({ status: newStatus, follow_up_created: true, due_date: date });
      close();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!lead || !newStatus) return null;
  const supportsFollowUp = !!defaults;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="status-followup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {statusAlreadyPersisted ? 'Schedule follow-up' : 'Update status'}
            <Badge className={`${getStatusBadgeClass(newStatus)} text-xs`}>{newStatus}</Badge>
          </DialogTitle>
          <DialogDescription>
            {statusAlreadyPersisted
              ? `Status already saved as ${newStatus}. A follow-up is suggested below — confirm or skip.`
              : (supportsFollowUp
                  ? 'A follow-up is suggested below. Confirm or skip.'
                  : 'No follow-up suggested for this status.')}
            <span className="block text-zinc-500 mt-1">
              Lead: <strong>{lead.full_name || lead.company_name || lead.id.slice(0, 8)}</strong>
            </span>
          </DialogDescription>
        </DialogHeader>

        {supportsFollowUp && (
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Type</Label>
                <SearchableSelect value={type} onValueChange={setType} options={FOLLOW_UP_TYPE_OPTIONS} placeholder="Select" data-testid="followup-type" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Date</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm" data-testid="followup-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Time</Label>
                <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="rounded-sm" data-testid="followup-time" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Notes</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="rounded-sm text-sm"
                data-testid="followup-notes"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={close} disabled={saving} data-testid="status-followup-cancel">Cancel</Button>
          {supportsFollowUp && (
            <Button variant="outline" onClick={persistStatusOnly} disabled={saving} data-testid="status-followup-skip">
              {statusAlreadyPersisted ? 'Skip — no follow-up' : 'Skip follow-up'}
            </Button>
          )}
          <Button
            onClick={supportsFollowUp ? persistStatusAndFollowUp : persistStatusOnly}
            disabled={saving}
            className="bg-[#002FA7] text-white"
            data-testid="status-followup-save"
          >
            {saving
              ? 'Saving…'
              : statusAlreadyPersisted
                ? 'Schedule follow-up'
                : (supportsFollowUp ? 'Save status + follow-up' : 'Save status')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
